






























import {Component, Emit, Prop, Watch} from "vue-property-decorator";
import AsiDialogForm from "@/components/common/AsiDialogForm.vue";
import AsiDialogFormLayout from "@/components/common/AsiDialogFormLayout.vue";
import AsiDatePicker from "@/components/common/AsiDatePicker.vue";
import AsiTimePicker from "@/components/common/AsiTimePicker.vue";
import {AxiosError} from "axios";
import Report from "@/models/report/Report";
import ReportUpdate from "@/models/report/ReportUpdate";
import {toISODateTimeString} from "@/helpers/functions_asi";
import AsiSelect from "@/components/common/AsiSelect";
import AsiFadeTransition from "@/components/common/AsiFadeTransition.vue";
import {ValidationRule} from "@/declarations/ValidationRule";
import AsiAlert from "@/components/common/AsiAlert.vue";
import {Permissions} from "@/helpers/constants";

@Component({
	components: {
		AsiAlert,
		AsiFadeTransition,
		AsiSelect,
		AsiDialogFormLayout,
		AsiDatePicker,
		AsiTimePicker
	}
})
export default class ReportUpdateDialogDateAndTime extends AsiDialogForm {

	@Prop({type: Object, required: true})
	public report!: Report;

	@Prop({type: String, default: null})
	public date!: string;

	private model: ReportUpdate | null = null;
	private fromTimeInternal: string | null = null;
	private toTimeInternal: string | null = null;
	private breakDurationRules: ValidationRule[] = [];

	private get breakOptions(): { text: string; value: number }[] {
		const ret: { text: string; value: number }[] = [
			{
				text: this.$t('uiTerms.noBreak').toString(),
				value: 0,
			}
		];

		let cur = 5;
		let curHours: number;
		let curMinutes: number;
		while (cur <= 6 * 60) {
			curMinutes = cur % 60;
			curHours = Math.floor(cur / 60);
			ret.push({
				text: curHours + ':' + curMinutes.toString().padStart(2, '0'),
				value: cur,
			})
			cur += 5;
		}

		return ret;
	}

	private get isOverMidnight(): boolean {
		if (this.model === null) return false;
		const dFrom = new Date(this.model.fromTime);
		const dTo = new Date(this.model.toTime);
		return dFrom.getDate() !== dTo.getDate();
	}

	private get isOffice(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightOffice);
	}

	public created(): void {
		this.setReportInternal();
		this.recalculateInternalTimeFields();
		this.createBreakDurationRules();
	}

	@Emit('save')
	public save(event: Event): Event {
		return event;
	}

	@Emit('cancel')
	public cancel(event: Event): Event {
		this.setReportInternal();
		this.recalculateInternalTimeFields();
		const form = this.$refs.form as HTMLFormElement;
		this.reset([], form);
		return event;
	}

	private fromTimeChanged(value: string) {
		if (this.model === null) return;
		this.model.fromTime = this.createDateTimeIsoString(new Date(this.model.date), value);
		this.recalculateInternalTimeFields();
		this.createBreakDurationRules();
	}

	private toTimeChanged(value: string) {
		if (this.model === null) return;

		const d = new Date(this.model.date);
		if (this.fromTimeInternal !== null && value.localeCompare(this.fromTimeInternal) < 0) {
			d.setDate(d.getDate() + 1);
		}

		this.model.toTime = this.createDateTimeIsoString(d, value);
		this.recalculateInternalTimeFields();
		this.createBreakDurationRules();
	}

	private recalculateInternalTimeFields(): void {
		if (this.model === null) return;
		this.fromTimeInternal = this.$d(new Date(this.model.fromTime), 'timeShort');
		this.toTimeInternal = this.$d(new Date(this.model.toTime), 'timeShort');
	}

	private createBreakDurationRules(): void {
		Promise.resolve().then(() => {
			this.breakDurationRules = this.model?.rules('breakDuration') ?? [];
			const breakDurationInput = this.$refs.breakDurationInput as unknown as {validate(x: boolean): boolean} | undefined;
			if (undefined !== breakDurationInput) {
				breakDurationInput.validate(true);
			}
		});
	}

	// noinspection JSMethodCanBeStatic
	private allowedMinutes(min: number): boolean {
		return min % 5 === 0;
	}

	private createDateTimeIsoString(date: Date | null, time: string | null): string {
		if (this.model === null || date === null || time === null) return '';
		const timeParts = time.split(':');
		const d = new Date(date.valueOf());
		d.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);
		return toISODateTimeString(d);
	}

	@Watch('report', {deep: true})
	private onReportChanged(): void {
		this.setReportInternal();
		this.recalculateInternalTimeFields();
	}

	private setReportInternal(): void {
		if (this.model === null) {
			this.model = new ReportUpdate(this.report);
		} else {
			this.model.setDetailModel(this.report);
		}
	}

	private requestSave(event: Event): void {
		if (this.model === null) return;

		const form = this.$refs.form as HTMLFormElement;
		if (!form.validate()) return;

		this.loading = true;
		this.$reportService.update(this.report.id, this.model)
			.then(() => this.save(event))
			.catch((err: AxiosError) => {
				this.errors = err.response?.data;
			})
			.finally(() => {
				this.loading = false;
			});
	}

}
